/**
 * SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/layouts/TenantAdminLayout.tsx
 * PURPOSE: SC-002-TA-HOME app shell — sidebar 240px + vùng nội dung, đúng
 *          WIREFRAME-V0-MASTER-REGISTRY L515-541 (4 mục menu không icon,
 *          header = tên tổ chức + "Quản trị tổ chức", footer user + Đăng xuất,
 *          active ◀ + viền trái đậm theo D8, ☰ drawer + overlay <1024px theo R1/R2)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-07-30
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + DD-0002 RBAC
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * ISS-0016 remediation (2026-07-30, @coder_frontend):
 * - Menu rút về đúng 4 mục registry, bỏ icon, nhãn VI đúng registry.
 * - 6 route ngoài scope v0 (employees, job-families, pay-grades, analytics,
 *   security, bot-config) ẨN khỏi menu theo quyết định OQ-3 (route vẫn tồn tại
 *   trong App.tsx, chỉ không hiển thị trên sidebar release v0).
 * - Header bind tên tổ chức thật (root node cây org) + "Quản trị tổ chức".
 * - Bỏ collapse ←/→ (ngoài spec); bổ sung ☰ + drawer + overlay <1024px (R1/R2).
 *
 * @business-context
 * - Domain: Tenant Admin navigation — shell chung mọi màn Quản trị tổ chức v0
 * - Impact: UX Critical — user thấy shell này ở MỌI màn TA
 * - Stakeholders: Tenant Admins, Tenant Owners
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Layout Component)
 * - Pattern: Sidebar cố định 240px (≥1024px) / ☰ drawer + overlay (<1024px)
 * - Dependencies: React Router, useAuthStore, useOrganizationTree, NQH tokens
 *
 * @cross-references
 * - Spec SSOT: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md (SC-002, L515-541; D8 L162; R1/R2 L209-236)
 * - Review: temp/REVIEW-UI-DIVERGENCE-2026-07-30.md (§2 F-1 / ISS-0016)
 * - Mockup hifi: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/tenant-admin/home.html
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import { useOrganizationTree } from "../hooks/useOrganizationTree";

interface MenuItem {
  path: string;
  label: string;
}

/** SC-002 L519-530: đúng 4 mục, nhãn VI nguyên văn registry, KHÔNG icon */
const TA_MENU: MenuItem[] = [
  { path: "/tenant-admin/dashboard", label: "Bảng điều khiển" },
  { path: "/tenant-admin/users", label: "Người dùng" },
  { path: "/tenant-admin/organizations", label: "Cơ cấu tổ chức" },
  { path: "/tenant-admin/settings", label: "Cài đặt tổ chức" },
];

const COFFEE_500 = "var(--nqh-coffee-500, #d97706)";
const COFFEE_50 = "var(--nqh-coffee-50, #fdf6ef)";

const TenantAdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, userRole, tenantKey, logout } = useAuthStore();

  // R1/R2: <1024px sidebar ẩn sau nút ☰ → drawer phủ trái + overlay đóng được
  const [isNarrow, setIsNarrow] = useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = (e: MediaQueryListEvent) => {
      setIsNarrow(e.matches);
      if (!e.matches) setDrawerOpen(false);
    };
    setIsNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // [1] Tên tổ chức thật — root node cây org (Tập đoàn), fallback tenantKey
  const { tree } = useOrganizationTree();
  const orgName = tree[0]?.name_vietnamese || tree[0]?.name || tenantKey || "Tổ chức";

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  if (userRole !== "TENANT_ADMIN" && userRole !== "TENANT_OWNER") {
    return <Navigate to="/dashboard/staff" replace />;
  }

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const sidebar = (
    <aside
      style={{
        width: "240px",
        background: "white",
        borderRight: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 60,
        boxShadow: "1px 0 3px rgba(0,0,0,0.05)",
        transition: "transform 0.2s ease",
        transform: isNarrow && !drawerOpen ? "translateX(-100%)" : "translateX(0)",
      }}
      aria-label="Điều hướng quản trị tổ chức"
    >
      {/* [1] Header — tên tổ chức thật + "Quản trị tổ chức" */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #e5e7eb",
          minHeight: "64px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: "bold", color: "#1f2937" }}>
          {orgName}
        </div>
        <div style={{ fontSize: "12px", color: "#6b7280" }}>Quản trị tổ chức</div>
      </div>

      {/* [2]-[5] Menu — không icon; active = ◀ cuối dòng + viền trái đậm (D8) */}
      <nav style={{ flex: 1, padding: "8px 0" }}>
        {TA_MENU.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              aria-current={active ? "page" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "10px 20px",
                border: "none",
                background: active ? COFFEE_50 : "transparent",
                borderLeft: active ? `3px solid ${COFFEE_500}` : "3px solid transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: active ? "600" : "400",
                color: active ? COFFEE_500 : "#374151",
                textAlign: "left",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "#f9fafb";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              <span>{item.label}</span>
              {active && <span aria-hidden="true">◀</span>}
            </button>
          );
        })}
      </nav>

      {/* [6a] Tên user + [6b] Đăng xuất */}
      <div style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb" }}>
        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#1f2937" }}>
            {user?.full_name || "Quản trị viên"}
          </div>
          <div style={{ fontSize: "11px", color: "#9ca3af" }}>Quản trị viên</div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: "100%",
            padding: "8px 0",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "13px",
            color: "#dc2626",
            textAlign: "left",
          }}
        >
          Đăng xuất
        </button>
      </div>
    </aside>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f3f4f6" }}>
      {sidebar}

      {/* R1/R2: overlay đóng drawer khi <1024px */}
      {isNarrow && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 55,
          }}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          marginLeft: isNarrow ? 0 : "240px",
          transition: "margin-left 0.2s ease",
          minHeight: "100vh",
        }}
      >
        {/* R1/R2: nút ☰ mở drawer khi <1024px */}
        {isNarrow && (
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Mở menu điều hướng"
            style={{
              position: "sticky",
              top: "8px",
              margin: "8px 0 0 8px",
              zIndex: 40,
              padding: "8px 12px",
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              color: "#374151",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            ☰
          </button>
        )}
        <Outlet />
      </main>
    </div>
  );
};

export default TenantAdminLayout;
